import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Timeline,
  Typography,
  Tag,
  Space,
  Collapse,
  Skeleton,
  Empty,
} from "antd";
import { HistoryOutlined, RollbackOutlined } from "@ant-design/icons";
import { Button } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";
import { useConfirm } from "../../../hooks/useConfirm";

const { Text, Paragraph } = Typography;

const VersionsTab = ({ agent, onRestored }) => {
  const { mutate } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;
  const { confirmAction } = useConfirm();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await mutateRef.current("fetchAgentVersions", {
        method: "get",
        endpoint: `/v1/agents/${agent.agentId}/versions`,
      });
      setVersions(data?.data || []);
    } catch {
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [agent.agentId]);

  useEffect(() => {
    load();
  }, [load]);

  const onRollback = (v) => {
    confirmAction({
      key: "rollbackAgent",
      method: "post",
      endpoint: `/v1/agents/${agent.agentId}/versions/${v.version}/rollback`,
      title: `Rollback to v${v.version}`,
      content: `This copies v${v.version}'s settings back into the agent and creates a new version. Existing keys keep working.`,
      okText: "Rollback",
      onSuccess: () => {
        load();
        onRestored?.();
      },
    });
  };

  if (loading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (versions.length === 0)
    return <Empty description="No versions yet" />;

  return (
    <div>
      <Space
        align="center"
        style={{ marginBottom: 16 }}
      >
        <HistoryOutlined />
        <Text strong>Version history</Text>
        <Text type="secondary">
          Every configuration save creates an immutable row. Current is v
          {agent.currentVersion}.
        </Text>
      </Space>

      <Timeline
        mode="left"
        items={versions.map((v) => ({
          color: v.version === agent.currentVersion ? "blue" : "gray",
          label: (
            <Text type="secondary">
              {new Date(v.createdAt).toLocaleString()}
            </Text>
          ),
          children: (
            <div>
              <Space wrap style={{ marginBottom: 8 }}>
                <Text strong>v{v.version}</Text>
                {v.version === agent.currentVersion && (
                  <Tag color="blue">Current</Tag>
                )}
                <Tag>{v.provider}</Tag>
                {v.model && <Text code>{v.model}</Text>}
                {v.createdBy && (
                  <Text type="secondary">by {v.createdBy}</Text>
                )}
                {v.version !== agent.currentVersion && (
                  <Button
                    size="small"
                    variant="ghost"
                    icon={<RollbackOutlined />}
                    onClick={() => onRollback(v)}
                  >
                    Rollback
                  </Button>
                )}
              </Space>
              {v.notes && (
                <Paragraph type="secondary" style={{ margin: 0 }}>
                  {v.notes}
                </Paragraph>
              )}
              {v.systemPrompt && (
                <Collapse
                  ghost
                  items={[
                    {
                      key: "prompt",
                      label: "Show system prompt",
                      children: (
                        <pre
                          style={{
                            whiteSpace: "pre-wrap",
                            fontSize: 12,
                            background: "rgba(0,0,0,0.04)",
                            padding: 8,
                            borderRadius: 4,
                            margin: 0,
                          }}
                        >
                          {v.systemPrompt}
                        </pre>
                      ),
                    },
                  ]}
                />
              )}
            </div>
          ),
        }))}
      />
    </div>
  );
};

export default VersionsTab;
